import { isAxiosError, isCancel } from 'axios'

/**
 * Text chyby z API pre používateľa — jediné miesto, ktoré číta telo chybovej odpovede.
 *
 * Tvar tela (HttpExceptionFilter v apps/api): `{ statusCode, error, details?, path, timestamp }`,
 * kde `error` je veta pre používateľa alebo kód (`NO_CREDITS`, `LAST_OWNER`…); `message` je len
 * poistka pre telá mimo filtra.
 *
 * Kto chybu zobrazí: globálny toast v interceptore (lib/api.ts). Volajúci, ktorý ju ukáže sám
 * (inline vo formulári, vlastný text, súhrn hromadnej akcie), pošle request so
 * `{ skipErrorToast: true }` a text si vezme odtiaľto.
 */

/** Známe kódy a technické texty servera → slovenský text. */
const KNOWN_ERROR_MESSAGES: Record<string, string> = {
  DEMO_TENANT_EXPORT_DISABLED: 'V demo účte nie je export na portály dostupný.',
  'ThrottlerException: Too Many Requests': 'Príliš veľa pokusov. Skúste to o chvíľu.',
  // multer limit fileSize
  'File too large': 'Súbor je príliš veľký.',
}

const NETWORK_ERROR_MESSAGE = 'Nepodarilo sa spojiť so serverom. Skontrolujte pripojenie.'
const DEFAULT_ERROR_MESSAGE = 'Operácia zlyhala.'

// Kód sa používateľovi neukáže surový — preloží ho KNOWN_ERROR_MESSAGES alebo mapa volajúceho.
const ERROR_CODE = /^[A-Z][A-Z0-9_]*$/
// 403 z RolesGuard / RoleAllowlistGuard nesie technický text s rolami.
const GUARD_FORBIDDEN = /^(Prístup zamietnutý|Endpoint not permitted|Forbidden)/

function errorText(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const { error, message } = body as { error?: unknown; message?: unknown }
  const text = error ?? message
  return typeof text === 'string' && text ? text : undefined
}

/** Surový `error` z tela chybovej odpovede (kód alebo veta) — pre vlastné mapy kódov. */
export function getApiErrorCode(err: unknown): string | undefined {
  return isAxiosError(err) ? errorText(err.response?.data) : undefined
}

/** Text chyby pre status a telo odpovede (aj pre `fetch` mimo axiosu). */
export function describeApiError(status: number, body: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  const text = errorText(body)
  if (text && KNOWN_ERROR_MESSAGES[text]) return KNOWN_ERROR_MESSAGES[text]
  if (status >= 500) return 'Chyba servera. Skúste to prosím neskôr.'
  if (status === 404) return 'Záznam sa nenašiel.'
  if (text && !ERROR_CODE.test(text) && !GUARD_FORBIDDEN.test(text)) return text
  if (status === 403) return 'Nemáte oprávnenie na túto akciu.'
  if (status === 409) return 'Konflikt — záznam nie je možné upraviť.'
  return fallback
}

/** Text chyby axios requestu; `fallback`, keď server nepovedal nič zrozumiteľné. */
export function getApiErrorMessage(err: unknown, fallback = DEFAULT_ERROR_MESSAGE): string {
  if (!isAxiosError(err) || isCancel(err)) return fallback
  if (!err.response) return NETWORK_ERROR_MESSAGE
  return describeApiError(err.response.status, err.response.data, fallback)
}

/** Text chyby prvého zlyhaného requestu hromadnej akcie (`Promise.allSettled`) pre súhrnný toast. */
export function getFirstRejectionMessage(results: PromiseSettledResult<unknown>[]): string {
  return getApiErrorMessage(results.find((r): r is PromiseRejectedResult => r.status === 'rejected')?.reason)
}
